import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import HealthAssistantCard from "src/components/app/review-state/HealthAssistantCard";
import Loader from "src/components/common/Loader";
import environment from "src/constants/environment";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as journeyActions from "src/redux/slices/journeySlice";
import { STORAGE_KEYS } from "src/constants/common";
import analytics from "src/helpers/analytics";
import { SCREEN_NAMES } from "src/helpers/analytics/events";
import storage from "src/helpers/storage";
import { setLayoutProperties } from "src/redux/slices/configSlice";
import { sendStatusBarColor } from "src/helpers/common";

const HealthAssistantScreenIframe: React.FC = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const { patient } = useAppSelector((state) => state.auth);
  const params = useParams();
  const [searchParams] = useSearchParams();

  const supportProgramKey = params["support-program-key"];
  const paidUser = Number(patient?.data?.paidUser) === 1;
  const loggerType = searchParams.get("loggerType");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const getCyclesData = () => {
    const payload = {
      patient_id: patient?.data?.patient_id,
      records_needed: 1,
    };
    new Promise((resolve, reject) => {
      dispatch(
        journeyActions.getCyclesRequest({
          resolve,
          reject,
          payload,
        })
      );
    })
      .then((res: any) => {
        getUrl(res?.data?.[0]?.patient_visit_id);
      })
      .catch((error: any) => {
        console.error("Error fetching cycles data", error);
        getUrl();
      });
    // .finally(() => {
    //   getUrl();
    // });
  };

  useEffect(() => {
    // page view
    analytics.pageView(SCREEN_NAMES.HEALTH_ASSISTANT_SCREEN);
  }, []);

  // TEST WIRING: this screen is always white (see DynamicScreen's bg-white
  // for "health-assistant-screen"), so report that as the native status bar
  // color while it's mounted.
  useEffect(() => sendStatusBarColor("#ffffff", "dark-content"), []);

  useEffect(() => {
    if (patient?.data) {
      getCyclesData();
    }
  }, [patient]);

  useEffect(() => {
    // reset loader when URL changes
    if (url) setIframeLoaded(false);
  }, [url]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const eventData: unknown = event.data;

      // expected shape from iframe:
      // window.parent.postMessage({ action: string, data?: any }, "*")
      if (
        !eventData ||
        typeof eventData !== "object" ||
        !("action" in eventData)
      ) {
        return;
      }

      const { action, data: payload } = eventData as {
        action?: unknown;
        data?: any;
      };

      if (typeof action !== "string" || !action) {
        console.warn("WebView message missing/invalid action:", eventData);
        return;
      }

      switch (action) {
        case "NAVIGATE_TO_MEDICATIONS":
          navigate(
            `/app/${supportProgramKey}/medication-log-details-screen?log=medication-logs`
          );
          break;

        case "INPUT_FOCUSED":
          dispatch(setLayoutProperties({ showBottomBar: false }));
          break;

        case "INPUT_BLURRED":
          dispatch(setLayoutProperties({ showBottomBar: true }));
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Handle viewport resize for WebView keyboard: when keyboard opens we constrain
  // the iframe container to the visible viewport; when it closes we clear inline
  // styles so the container uses h-full and doesn't extend past the layout area.
  const KEYBOARD_HEIGHT_THRESHOLD = 100;

  useEffect(() => {
    const viewport = window.visualViewport;

    if (!viewport) return;

    const handleViewportResize = () => {
      if (!viewport || !containerRef.current) return;

      const heightDiff = window.innerHeight - viewport.height;
      const keyboardOpen = heightDiff > KEYBOARD_HEIGHT_THRESHOLD;

      if (keyboardOpen) {
        const viewportHeight = viewport.height;
        const offsetTop = viewport.offsetTop || 0;
        containerRef.current.style.height = `${viewportHeight}px`;
        containerRef.current.style.transform = `translateY(${offsetTop}px)`;
      } else {
        // Keyboard closed: clear inline height/transform so container uses
        // h-full (100% of parent) and doesn't extend beyond the layout area.
        containerRef.current.style.height = "";
        containerRef.current.style.transform = "";
      }
    };

    const handleViewportScroll = () => {
      if (!viewport || !containerRef.current) return;
      const heightDiff = window.innerHeight - viewport.height;
      if (heightDiff <= KEYBOARD_HEIGHT_THRESHOLD) return;
      const offsetTop = viewport.offsetTop || 0;
      containerRef.current.style.transform = `translateY(${offsetTop}px)`;
    };

    viewport.addEventListener("resize", handleViewportResize);
    viewport.addEventListener("scroll", handleViewportScroll);

    return () => {
      viewport.removeEventListener("resize", handleViewportResize);
      viewport.removeEventListener("scroll", handleViewportScroll);
    };
  }, []);

  const getUrl = (patientVisitId?: string) => {
    const token = storage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const baseUrl = environment?.HEALTH_ASSISTANT_DOMAIN_URL;
    let url = `${baseUrl}/health-assistant`;
    const themeColors = storage.getItem(STORAGE_KEYS.THEME_COLORS);
    const params: {
      patientId: string;
      patientVisitId: string;
      patientName: string;
      platform?: string;
      loggerType?: string;
      token?: string;
      themeColors?: string;
      support_program_key?: string;
    } = {
      patientId: patient?.data?.patient_id || "",
      patientVisitId: patientVisitId || "",
      patientName: patient?.data?.name || "",
      platform: "GoodFlipCare",
      token: token || "",
      themeColors: themeColors || "",
      support_program_key: supportProgramKey || "",
    };
    if (loggerType) params.loggerType = loggerType;

    const queryString = new URLSearchParams(params).toString();
    url = `${url}?${queryString}`;
    setUrl(url);
  };

  if (!paidUser) {
    return (
      <div className="flex flex-col gap-4">
        <HealthAssistantCard />
      </div>
    );
  }
  //
  return (
    <div
      ref={containerRef}
      className="h-full w-full"
      style={{
        position: "relative",
        overflow: "hidden",
        transition: "transform 0.3s ease-out, height 0.3s ease-out",
      }}
    >
      {url && !iframeLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-white z-10">
          <Loader size="lg" />
        </div>
      )}
      {url && (
        <iframe
          ref={iframeRef}
          src={url}
          onLoad={() => setIframeLoaded(true)}
          width="100%"
          height="100%"
          style={{
            border: "none",
            display: "block",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: iframeLoaded ? 1 : 0,
            transition: "opacity 150ms ease-out",
          }}
          allow="
            camera;
            microphone;
            geolocation;
            clipboard-read;
            clipboard-write;
            fullscreen;
            autoplay;
            payment;
            accelerometer;
            gyroscope;
            magnetometer;
            midi;
            picture-in-picture;
            encrypted-media;
            display-capture
          "
        />
      )}
    </div>
  );
};

export default HealthAssistantScreenIframe;
